import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { errText } from "../../lib/api";
import { Sparkle, CheckCircle, X, ArrowUpRight, CircleNotch, Warning } from "@phosphor-icons/react";

const CAT_STYLE = {
  advisory: "bg-indigo-50 text-indigo-800 border-indigo-200",
  compliance: "bg-slate-50 text-slate-800 border-slate-200",
  accounting: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ai: "bg-cyan-50 text-cyan-800 border-cyan-200",
  tax: "bg-blue-50 text-blue-800 border-blue-200",
  custom: "bg-rose-50 text-rose-800 border-rose-200",
};

const STALE_MS = 24 * 60 * 60 * 1000; // 24h SLA — mirrors ServiceInboxPulse

/** Pending client-initiated service requests — approve one-click creates an
 * engagement + auto-generates an engagement letter envelope. */
export function ServiceRequestInbox({ requests = [] }) {
  const qc = useQueryClient();

  const approve = useMutation({
    mutationFn: (id) => api.post(`/service-requests/${id}/approve`, {}),
    onSuccess: (r, id) => {
      toast.success("Approved — engagement + engagement letter created");
      qc.invalidateQueries({ queryKey: ["flightdeck"] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const decline = useMutation({
    mutationFn: ({ id, reason }) => api.post(`/service-requests/${id}/decline`, { reason }),
    onSuccess: () => {
      toast.success("Request declined");
      qc.invalidateQueries({ queryKey: ["flightdeck"] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="flightdeck-service-inbox">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Sparkle size={13} weight="fill" className="text-emerald-600" />
          Pending service requests
        </h3>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border bg-zinc-50 border-zinc-200 text-zinc-600" data-testid="flightdeck-service-inbox-count">
          {requests.length}
        </span>
      </div>
      {requests.length === 0 ? (
        <p className="text-[11.5px] text-zinc-400 italic">No pending requests. Clients can request a new service from their portal sidebar.</p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {requests.map((r) => {
            const ageMs = Date.now() - new Date(r.created_at).getTime();
            const stale = ageMs > STALE_MS;
            const ageLabel = stale ? `${Math.floor(ageMs / STALE_MS)}d overdue` : "";
            return (
            <li
              key={r.id}
              className={`rounded-md border p-2.5 transition-colors ${stale ? "border-rose-200 bg-rose-50/40" : "border-zinc-200 bg-zinc-50/40"}`}
              data-testid={`flightdeck-svc-request-${r.id}`}
              data-stale={stale ? "true" : "false"}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1 py-0.5 ${CAT_STYLE[r.service_category] || CAT_STYLE.custom}`}>
                      {r.service_category}
                    </span>
                    <p className="text-sm font-semibold text-slate-900">{r.service_name}</p>
                    {stale && (
                      <span
                        data-testid={`flightdeck-svc-stale-${r.id}`}
                        title="Pending more than 24 hours — please action or decline"
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold text-rose-800 bg-rose-100 border border-rose-200 rounded-sm px-1.5 py-0.5"
                      >
                        <Warning size={9} weight="fill" /> {ageLabel}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-0.5">
                    <Link to={`/clients/${r.client_id}`} className="text-slate-800 hover:underline" data-testid={`flightdeck-svc-client-${r.id}`}>
                      {r.client_name}
                    </Link>
                    <span className="text-zinc-400"> · {r.client_email}</span>
                  </p>
                  {r.notes && (
                    <p className="text-[11px] text-zinc-700 mt-1 italic bg-white/70 rounded-sm border border-zinc-100 px-2 py-1">
                      "{r.notes.length > 200 ? r.notes.slice(0, 200) + "…" : r.notes}"
                    </p>
                  )}
                  <p className={`text-[10px] mt-1 ${stale ? "text-rose-600 font-medium" : "text-zinc-400"}`}>
                    Requested {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`flightdeck-svc-approve-${r.id}`}
                    disabled={approve.isPending}
                    onClick={() => approve.mutate(r.id)}
                    className="inline-flex items-center gap-1 text-[10.5px] font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-2 py-1 transition-colors duration-150 disabled:opacity-50"
                  >
                    {approve.isPending ? <CircleNotch size={10} className="animate-spin" /> : <CheckCircle size={10} weight="fill" />} Approve
                  </button>
                  <button
                    data-testid={`flightdeck-svc-decline-${r.id}`}
                    disabled={decline.isPending}
                    onClick={() => {
                      const reason = window.prompt("Reason for declining (shared with the client)?", "");
                      if (reason === null) return;
                      decline.mutate({ id: r.id, reason });
                    }}
                    className="inline-flex items-center gap-1 text-[10.5px] font-medium text-slate-700 bg-white border border-zinc-300 hover:bg-zinc-50 rounded-md px-2 py-1 transition-colors duration-150 disabled:opacity-50"
                  >
                    <X size={10} weight="bold" /> Decline
                  </button>
                </div>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
